import { useImperativeHandle, useRef } from 'react';

import type { RefObject } from 'react';
import type {
  FieldErrors,
  FieldValues,
  UseFormHandleSubmit,
} from 'react-hook-form';

export type SubmitResult<TResult extends FieldValues> = [
  TResult | undefined,
  FieldErrors<TResult> | undefined,
];

export type FormControlRef<TResult extends FieldValues> = {
  submit: () => Promise<SubmitResult<TResult>>;
};

export const useImperativeForm = <
  T extends FieldValues,
  TError extends FieldValues,
>(
  controlRef: RefObject<FormControlRef<T>>,
  errors: FieldErrors<TError>,
  wrapper: UseFormHandleSubmit<T>,
  type?: string,
) => {
  type SubmitReturn = SubmitResult<T>;

  const formRef = useRef<HTMLFormElement>(null);
  const resolverRef = useRef<(...args: SubmitReturn) => void>();

  useImperativeHandle(controlRef, () => {
    async function submit() {
      if (Object.keys(errors).length > 0) {
        return [undefined, errors] as SubmitReturn;
      }
      const promise = new Promise<SubmitReturn>((resolve) => {
        resolverRef.current = (result, errors) => {
          resolve([result, errors]);
        };
      });

      formRef?.current?.requestSubmit?.();

      return await promise;
    }
    return {
      submit,
      type,
    };
  }, [type]);

  const onSubmit = wrapper(
    (data) => {
      resolverRef.current?.(data, undefined);
    },
    (errors) => {
      resolverRef.current?.(undefined, errors);
    },
  );

  return { ref: formRef, onSubmit };
};
